import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';

/**
 * App-wide error boundary. Catches render/runtime errors anywhere in the tree
 * and shows a friendly fallback instead of letting the page crash (or the
 * red "Uncaught runtime errors" overlay take over). Lets the user recover
 * without losing the whole session.
 */
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        // Keep a record for debugging without surfacing a crash to the user.
        // eslint-disable-next-line no-console
        console.error('Caught by ErrorBoundary:', error, errorInfo);
    }

    handleReload = () => {
        this.setState({ hasError: false });
        window.location.reload();
    };

    render() {
        if (!this.state.hasError) return this.props.children;

        return (
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    gap: 2,
                    px: 3,
                    background: '#FFFFFF',
                }}
            >
                <Box
                    sx={{
                        width: 72, height: 72, borderRadius: '50%',
                        background: 'rgba(167,44,104,0.08)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                >
                    <ReportProblemOutlinedIcon sx={{ fontSize: 36, color: '#A72C68' }} />
                </Box>
                <Typography sx={{ fontSize: '20px', fontWeight: 800, color: '#1a1a2e' }}>
                    Something went wrong
                </Typography>
                <Typography sx={{ fontSize: '14px', color: '#6B7280', maxWidth: 360 }}>
                    An unexpected error occurred. Please reload the page — your session is still active.
                </Typography>
                <Button
                    onClick={this.handleReload}
                    sx={{
                        mt: 1,
                        background: 'linear-gradient(135deg, #27AE60 0%, #1E8449 100%)',
                        color: '#fff', border: '1px solid #27AE60', boxShadow: 'none',
                        borderRadius: '10px', textTransform: 'none',
                        fontWeight: 700, px: 3, py: 1,
                        '&:hover': {
                            background: '#219653', color: '#fff',
                            border: '1px solid #27AE60', boxShadow: 'none',
                        },
                    }}
                >
                    Reload page
                </Button>
            </Box>
        );
    }
}

export default ErrorBoundary;
